import { useState } from 'react';
import { useInspector } from '~/lib/stores/ui-store';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/components/ui/tabs';
import { EventPanel } from '~/features/events/event-panel';
import { MemorySnapshot } from '~/features/inspector/memory-snapshot';
import { ToolCallLog } from '~/features/inspector/tool-call-log';
import { BackgroundTasks } from '~/features/inspector/background-tasks';
import { UpcomingCrons } from '~/features/inspector/upcoming-crons';
import { ApprovalCard } from '~/features/approvals/approval-card';
import { useApprovals } from '~/hooks/queries/use-approvals';
import { useApprove, useReject } from '~/hooks/mutations/use-approval-actions';

type InspectorTab = 'live' | 'memory' | 'log';

function SectionLabel({ children, count }: { children: React.ReactNode; count?: number | null }) {
  return (
    <div className="flex items-center justify-between px-3 pt-3 pb-1.5">
      <span className="text-[length:var(--text-sm)] text-muted-foreground font-medium">
        {children}
      </span>
      {count != null && (
        <span className="text-[length:var(--text-sm)] font-mono text-muted-foreground/70 tabular-nums">{count}</span>
      )}
    </div>
  );
}

function PendingApprovals() {
  const { data: approvals = [], isLoading } = useApprovals();
  const approveMut = useApprove();
  const rejectMut = useReject();

  if (isLoading) {
    return <div className="px-3 py-1 text-[length:var(--text-sm)] text-muted-foreground/70">Loading…</div>;
  }
  if (approvals.length === 0) {
    return <div className="px-3 py-1 text-[length:var(--text-sm)] text-muted-foreground/70">No pending approvals.</div>;
  }
  return (
    <div className="px-3 pb-2 space-y-2">
      {approvals.map((a) => (
        <ApprovalCard
          key={a.id}
          approval={a}
          onApprove={() => approveMut.mutate(a.id)}
          onReject={() => rejectMut.mutate(a.id)}
        />
      ))}
    </div>
  );
}

export function InspectorPanel() {
  const { open } = useInspector();
  const { data: approvals = [] } = useApprovals();
  const [tab, setTab] = useState<InspectorTab>('live');

  if (!open) return null;

  return (
    <aside className="w-80 min-w-80 overflow-hidden rounded-[var(--frame-radius)] border border-border bg-card shadow-[var(--shadow-sm)] flex flex-col">
      <div className="px-3 py-2.5 border-b border-border shrink-0 flex items-center justify-between">
        <h2 className="text-[length:var(--text-md)] font-semibold text-foreground tracking-[-0.015em]">Inspector</h2>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as InspectorTab)}
        className="flex-1 flex flex-col min-h-0"
      >
        <div className="px-3 pt-2 pb-1 border-b border-border shrink-0">
          {/* FRONT-2485: `sm`, the shared small size, instead of per-trigger overrides. */}
          <TabsList size="sm">
            <TabsTrigger value="live">Live</TabsTrigger>
            <TabsTrigger value="memory">Memory</TabsTrigger>
            <TabsTrigger value="log">Log</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="live" className="flex-1 overflow-y-auto m-0 p-0">
          <SectionLabel>Now</SectionLabel>
          <div className="px-3 pb-2">
            <BackgroundTasks />
          </div>

          <div className="border-t border-border" />
          <SectionLabel>Upcoming</SectionLabel>
          <div className="px-3 pb-2">
            <UpcomingCrons />
          </div>

          <div className="border-t border-border" />
          <SectionLabel count={approvals.length}>Pending approval</SectionLabel>
          <PendingApprovals />

          <div className="border-t border-border" />
          <SectionLabel>Activity</SectionLabel>
          <div className="-mt-1">
            <EventPanel />
          </div>
        </TabsContent>

        <TabsContent value="memory" className="flex-1 overflow-y-auto m-0 p-0">
          <SectionLabel>Snapshot</SectionLabel>
          <div className="px-3 pb-3">
            <MemorySnapshot />
          </div>
          <div className="border-t border-border" />
          <SectionLabel>Conflicts</SectionLabel>
          <div className="px-3 pb-3 text-[length:var(--text-sm)] text-muted-foreground/70">
            No open conflicts.
          </div>
        </TabsContent>

        <TabsContent value="log" className="flex-1 overflow-y-auto m-0 p-3">
          <ToolCallLog />
        </TabsContent>
      </Tabs>
    </aside>
  );
}
